import {
  useContext,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
} from "react";
import { FaCaretDown } from "react-icons/fa";
import AppContext from "../../context";
import {
  cmToIn,
  decimalInchesToNearest16thFraction,
  inchFractionsToCmDecimal,
} from "../../util/helpers";
import { IMPERIAL_ACTIONS } from "../../util/hooks/initDimensions";

const DimensionForm = ({
  imperial,
  widthError,
  depthError,
  heightError,
  handleSetImperial,
  handleSetWidthError,
  handleSetDepthError,
  handleSetHeightError,
  width,
  depth,
  height,
  setWidth,
  setDepth,
  setHeight,
  currentDimensions,
  setCurrentDimensions,
}) => {
  const {
    MAX_HEIGHT_DEFAULT,
    MIN_HEIGHT,
    MIN_WIDTH_DEPTH,
    MAX_WIDTH_DEPTH,
    dimensionsState,
    dispatchDimensions: dispatch,
  } = useContext(AppContext);

  const [initialLoad, setInitialLoad] = useState(true);
  const [dimensionsErrorMessage, setDimensionsErrorMessage] = useState(
    "The dimensions are not allowed for this material."
  );
  const widthFractionDropdownRef = useRef(null);
  const depthFractionDropdownRef = useRef(null);
  const heightFractionDropdownRef = useRef(null);
  const [widthFractionsDropdownActive, setWidthFractionsDropdownActive] =
    useState(false);
  const [depthFractionsDropdownActive, setDepthFractionsDropdownActive] =
    useState(false);
  const [heightFractionsDropdownActive, setHeightFractionsDropdownActive] =
    useState(false);

  const FRACTIONS_V2 = [
    { numerator: 0, denominator: 1 },
    { numerator: 1, denominator: 16 },
    { numerator: 1, denominator: 8 },
    { numerator: 3, denominator: 16 },
    { numerator: 1, denominator: 4 },
    { numerator: 5, denominator: 16 },
    { numerator: 3, denominator: 8 },
    { numerator: 7, denominator: 16 },
    { numerator: 1, denominator: 2 },
    { numerator: 9, denominator: 16 },
    { numerator: 5, denominator: 8 },
    { numerator: 11, denominator: 16 },
    { numerator: 3, denominator: 4 },
    { numerator: 13, denominator: 16 },
    { numerator: 7, denominator: 8 },
    { numerator: 15, denominator: 16 },
  ];

  useLayoutEffect(() => {
    let widthInput = document.getElementById("init_outside_width");
    let depthInput = document.getElementById("init_outside_depth");
    let heightInput = document.getElementById("init_outside_height");
    let widthValue = isNaN(widthInput.valueAsNumber)
      ? width
      : widthInput.valueAsNumber;
    let heightValue = isNaN(heightInput.valueAsNumber)
      ? height
      : heightInput.valueAsNumber;
    let depthValue = isNaN(depthInput.valueAsNumber)
      ? depth
      : depthInput.valueAsNumber;

    widthInput.value = widthValue.toString();
    depthInput.value = depthValue.toString();
    heightInput.value = heightValue.toString();
    const dimensionsObject = {
      width: {
        cm: widthValue,
        in: decimalInchesToNearest16thFraction(cmToIn(widthValue)),
      },
      height: {
        cm: heightValue,
        in: decimalInchesToNearest16thFraction(cmToIn(heightValue)),
      },
      depth: {
        cm: depthValue,
        in: decimalInchesToNearest16thFraction(cmToIn(depthValue)),
      },
    };
    setCurrentDimensions(dimensionsObject);
    setInitialLoad(false);
  }, []);

  useEffect(() => {
    if (imperial && currentDimensions) {
      dispatch({
        type: IMPERIAL_ACTIONS.SET_WIDTH_INTEGER,
        value: currentDimensions.width.in.integer,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_WIDTH_FRACTION,
        fraction: currentDimensions.width.in.fraction,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_WIDTH_CM,
        value: currentDimensions.width.cm,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_DEPTH_INTEGER,
        value: currentDimensions.depth.in.integer,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_DEPTH_FRACTION,
        fraction: currentDimensions.depth.in.fraction,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_DEPTH_CM,
        value: currentDimensions.depth.cm,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_HEIGHT_INTEGER,
        value: currentDimensions.height.in.integer,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_HEIGHT_FRACTION,
        fraction: currentDimensions.height.in.fraction,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_HEIGHT_CM,
        value: currentDimensions.height.cm,
      });
    }
  }, [currentDimensions]);

  useEffect(() => {
    const handleDOutsideClick = (event) => {
      if (
        depthFractionDropdownRef.current &&
        !depthFractionDropdownRef.current.contains(event.target)
      ) {
        setDepthFractionsDropdownActive(false);
      }
    };
    document.addEventListener("mousedown", handleDOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleDOutsideClick);
    };
  }, [depthFractionDropdownRef.current]);

  useEffect(() => {
    const handleWOutsideClick = (event) => {
      if (
        widthFractionDropdownRef.current &&
        !widthFractionDropdownRef.current.contains(event.target)
      ) {
        setWidthFractionsDropdownActive(false);
      }
    };
    document.addEventListener("mousedown", handleWOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleWOutsideClick);
    };
  }, [widthFractionDropdownRef.current]);

  useEffect(() => {
    const handleHOutsideClick = (event) => {
      if (
        heightFractionDropdownRef.current &&
        !heightFractionDropdownRef.current.contains(event.target)
      ) {
        setHeightFractionsDropdownActive(false);
      }
    };
    document.addEventListener("mousedown", handleHOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleHOutsideClick);
    };
  }, [heightFractionDropdownRef.current]);

  const handleChangeWidth = (unit, value) => {
    switch (unit) {
      case "imperial": {
        const widthInCm = inchFractionsToCmDecimal({
          integer: value.integer,
          fraction: value.fraction,
        });
        if (widthInCm >= MIN_WIDTH_DEPTH && widthInCm <= MAX_WIDTH_DEPTH) {
          handleSetWidthError(false);
          setWidth(parseFloat(widthInCm));
          window?.changeWidth?.();
        } else {
          handleSetWidthError(true);
          if (width <= MIN_WIDTH_DEPTH)
            setDimensionsErrorMessage(
              "This is the minimum width we can make an insert using 3/8″ material. For smaller inserts please contact us."
            );
          else
            setDimensionsErrorMessage(
              "This is the maximum width we can make an insert using 3/8″ material. For larger inserts please contact us."
            );
        }
        break;
      }
      default: {
        if (value >= MIN_WIDTH_DEPTH && value <= MAX_WIDTH_DEPTH) {
          handleSetWidthError(false);
          setWidth(parseFloat(value));
          window?.changeWidth?.();
        } else {
          handleSetWidthError(true);
          if (width <= MIN_WIDTH_DEPTH)
            setDimensionsErrorMessage(
              "This is the minimum width we can make an insert using 3/8″ material. For smaller inserts please contact us."
            );
          else
            setDimensionsErrorMessage(
              "This is the maximum width we can make an insert using 3/8″ material. For larger inserts please contact us."
            );
        }
      }
    }
  };

  const handleChangeDepth = (unit, value) => {
    switch (unit) {
      case "imperial": {
        const depthInCm = inchFractionsToCmDecimal({
          integer: value.integer,
          fraction: value.fraction,
        });
        if (depthInCm >= MIN_WIDTH_DEPTH && depthInCm <= MAX_WIDTH_DEPTH) {
          handleSetDepthError(false);
          setDepth(parseFloat(depthInCm));
        } else {
          handleSetDepthError(true);
          if (depth <= MIN_WIDTH_DEPTH)
            setDimensionsErrorMessage(
              "This is the minimum depth we can make an insert using 3/8″ material. For smaller inserts please contact us."
            );
          else
            setDimensionsErrorMessage(
              "This is the maximum depth we can make an insert using 3/8″ material. For larger inserts please contact us."
            );
        }
        break;
      }
      default: {
        if (value >= MIN_WIDTH_DEPTH && value <= MAX_WIDTH_DEPTH) {
          handleSetDepthError(false);
          setDepth(parseFloat(value));
        } else {
          handleSetDepthError(true);
          if (depth <= MIN_WIDTH_DEPTH)
            setDimensionsErrorMessage(
              "This is the minimum depth we can make an insert using 3/8″ material. For smaller inserts please contact us."
            );
          else
            setDimensionsErrorMessage(
              "This is the maximum depth we can make an insert using 3/8″ material. For larger inserts please contact us."
            );
        }
      }
    }
  };

  const handleChangeHeight = (unit, value) => {
    switch (unit) {
      case "imperial": {
        const heightInCm = inchFractionsToCmDecimal({
          integer: value.integer,
          fraction: value.fraction,
        });
        if (heightInCm >= MIN_HEIGHT && heightInCm <= MAX_HEIGHT_DEFAULT) {
          handleSetHeightError(false);
          setHeight(parseFloat(heightInCm));
        } else {
          handleSetHeightError(true);
          if (height <= MIN_HEIGHT)
            setDimensionsErrorMessage(
              "This is the minimum height we can make an insert using 3/8″ material. For smaller inserts please contact us."
            );
          else
            setDimensionsErrorMessage(
              "This is the maximum height we can make an insert using 3/8″ material. For larger inserts please contact us."
            );
        }
        break;
      }
      default: {
        if (value >= MIN_HEIGHT && value <= MAX_HEIGHT_DEFAULT) {
          handleSetHeightError(false);
          setHeight(parseFloat(value));
        } else {
          handleSetHeightError(true);
          if (height <= MIN_HEIGHT)
            setDimensionsErrorMessage(
              "This is the minimum height we can make an insert using 3/8″ material. For smaller inserts please contact us."
            );
          else
            setDimensionsErrorMessage(
              "This is the maximum height we can make an insert using 3/8″ material. For larger inserts please contact us."
            );
        }
      }
    }
  };

  const handleSwitchUnit = (checked) => {
    if (checked) {
      const widthInchFractions = decimalInchesToNearest16thFraction(
        cmToIn(width)
      );
      dispatch({
        type: IMPERIAL_ACTIONS.SET_WIDTH_INTEGER,
        value: widthInchFractions.integer,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_WIDTH_FRACTION,
        fraction: widthInchFractions.fraction,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_WIDTH_CM,
        value: width,
      });
      const depthInchFractions = decimalInchesToNearest16thFraction(
        cmToIn(depth)
      );
      dispatch({
        type: IMPERIAL_ACTIONS.SET_DEPTH_INTEGER,
        value: depthInchFractions.integer,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_DEPTH_FRACTION,
        fraction: depthInchFractions.fraction,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_DEPTH_CM,
        value: depth,
      });
      const heightInchFractions = decimalInchesToNearest16thFraction(
        cmToIn(height)
      );
      dispatch({
        type: IMPERIAL_ACTIONS.SET_HEIGHT_INTEGER,
        value: heightInchFractions.integer,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_HEIGHT_FRACTION,
        fraction: heightInchFractions.fraction,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_HEIGHT_CM,
        value: height,
      });
      handleSetImperial(true);
    } else handleSetImperial(false);
  };

  return (
    <>
      <div className="dimensionForm">
        <div className="dimensionForm--inner">
          <div className="dimensionForm--text-input">
            <label>
              Width
              {imperial && (
                <div className="is-flex is-align-items-center">
                  <input
                    className={`dimensionForm--inch-input ${
                      widthError ? "input-error" : ""
                    }`}
                    id="outside_width_inch_full"
                    type="number"
                    value={dimensionsState.init_width_integer}
                    onChange={(e) => {
                      dispatch({
                        type: IMPERIAL_ACTIONS.SET_WIDTH_INTEGER,
                        value: parseInt(e.target.value),
                      });
                      handleChangeWidth("imperial", {
                        fraction: dimensionsState.init_width_fraction,
                        integer: parseInt(e.target.value),
                      });
                    }}
                  />
                  <div
                    className={`dimensionForm--fraction-dropdown dropdown ${
                      widthFractionsDropdownActive ? "is-active" : ""
                    }`}
                    ref={widthFractionDropdownRef}
                  >
                    <div
                      className="dropdown-trigger"
                      style={{ overflow: "hidden" }}
                    >
                      <button
                        className={`button is-outlined reset-button is-light ${
                          widthError ? "input-error" : ""
                        }`}
                        id="outside_width_inch_fraction"
                        aria-haspopup="true"
                        aria-controls="init_width-fractions"
                        onClick={() => {
                          setWidthFractionsDropdownActive(
                            !widthFractionsDropdownActive
                          );
                        }}
                      >
                        {dimensionsState.init_width_fraction.numerator === 0 ? (
                          <span>0</span>
                        ) : (
                          <span>
                            {dimensionsState.init_width_fraction.numerator}/
                            {dimensionsState.init_width_fraction.denominator}
                          </span>
                        )}
                        <span className="icon is-small">
                          <FaCaretDown />
                        </span>
                      </button>
                    </div>
                    <div
                      className="dropdown-menu"
                      id="init_width-fractions"
                      role="menu"
                    >
                      <div className="dropdown-content has-text-black">
                        {FRACTIONS_V2.map((fraction, index) => (
                          <button
                            className="reset-button dropdown-item"
                            key={"width-fraction-" + index}
                            onClick={() => {
                              dispatch({
                                type: IMPERIAL_ACTIONS.SET_WIDTH_FRACTION,
                                fraction,
                              });
                              handleChangeWidth("imperial", {
                                fraction,
                                integer: dimensionsState.init_width_integer,
                              });
                              setWidthFractionsDropdownActive(false);
                            }}
                          >
                            {fraction.numerator === 0
                              ? "0"
                              : `${fraction.numerator}/${fraction.denominator}`}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <span className={`unit ${widthError ? "input-error" : ""}`}>
                    in
                  </span>
                </div>
              )}
              <div className={`is-flex ${imperial ? "is-hidden" : ""}`}>
                <input
                  className={`cm-input ${widthError ? "input-error" : ""}`}
                  type="number"
                  step="0.01"
                  min="0"
                  id="init_outside_width"
                  onChange={(e) => handleChangeWidth("metric", e.target.value)}
                />
                <span className={`unit ${widthError ? "input-error" : ""}`}>
                  cm
                </span>
              </div>
            </label>
          </div>
          <div className="dimensionForm--text-input">
            <label>
              Depth
              {imperial && (
                <div className="is-flex is-align-items-center">
                  <input
                    className={`dimensionForm--inch-input ${
                      depthError ? "input-error" : ""
                    }`}
                    id="outside_depth_inch_full"
                    type="number"
                    value={dimensionsState.init_depth_integer}
                    onChange={(e) => {
                      dispatch({
                        type: IMPERIAL_ACTIONS.SET_DEPTH_INTEGER,
                        value: parseInt(e.target.value),
                      });
                      handleChangeDepth("imperial", {
                        fraction: dimensionsState.init_depth_fraction,
                        integer: parseInt(e.target.value),
                      });
                    }}
                  />
                  <div
                    className={`dimensionForm--fraction-dropdown dropdown ${
                      depthFractionsDropdownActive ? "is-active" : ""
                    }`}
                    ref={depthFractionDropdownRef}
                  >
                    <div
                      className="dropdown-trigger"
                      style={{ overflow: "hidden" }}
                    >
                      <button
                        className={`button is-outlined reset-button is-light ${
                          depthError ? "input-error" : ""
                        }`}
                        id="outside_depth_inch_fraction"
                        aria-haspopup="true"
                        aria-controls="init_depth-fractions"
                        onClick={() => {
                          setDepthFractionsDropdownActive(
                            !depthFractionsDropdownActive
                          );
                        }}
                      >
                        {dimensionsState.init_depth_fraction.numerator === 0 ? (
                          <span>0</span>
                        ) : (
                          <span>
                            {dimensionsState.init_depth_fraction.numerator}/
                            {dimensionsState.init_depth_fraction.denominator}
                          </span>
                        )}
                        <span className="icon is-small">
                          <FaCaretDown></FaCaretDown>
                        </span>
                      </button>
                    </div>
                    <div
                      className="dropdown-menu"
                      id="init_depth-fractions"
                      role="menu"
                    >
                      <div className="dropdown-content has-text-black">
                        {FRACTIONS_V2.map((fraction, index) => (
                          <button
                            className="reset-button dropdown-item"
                            key={"depth-fraction-" + index}
                            onClick={() => {
                              dispatch({
                                type: IMPERIAL_ACTIONS.SET_DEPTH_FRACTION,
                                fraction,
                              });
                              handleChangeDepth("imperial", {
                                fraction,
                                integer: dimensionsState.init_depth_integer,
                              });
                              setDepthFractionsDropdownActive(false);
                            }}
                          >
                            {fraction.numerator === 0
                              ? "0"
                              : `${fraction.numerator}/${fraction.denominator}`}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <span className={`unit ${depthError ? "input-error" : ""}`}>
                    in
                  </span>
                </div>
              )}
              <div className={`is-flex ${imperial ? "is-hidden" : ""}`}>
                <input
                  className={`cm-input ${depthError ? "input-error" : ""}`}
                  type="number"
                  step="0.01"
                  min="0"
                  id="init_outside_depth"
                  onChange={(e) => handleChangeDepth("metric", e.target.value)}
                />
                <span className={`unit ${depthError ? "input-error" : ""}`}>
                  cm
                </span>
              </div>
            </label>
          </div>

          <div className="dimensionForm--text-input">
            <label>
              Height
              {imperial && (
                <div className="is-flex is-align-items-center">
                  <input
                    className={`dimensionForm--inch-input ${
                      heightError ? "input-error" : ""
                    }`}
                    id="outside_height_inch_full"
                    type="number"
                    value={dimensionsState.init_height_integer}
                    onChange={(e) => {
                      dispatch({
                        type: IMPERIAL_ACTIONS.SET_HEIGHT_INTEGER,
                        value: parseInt(e.target.value),
                      });
                      handleChangeHeight("imperial", {
                        fraction: dimensionsState.init_height_fraction,
                        integer: parseInt(e.target.value),
                      });
                    }}
                  />
                  <div
                    className={`dimensionForm--fraction-dropdown dropdown ${
                      heightFractionsDropdownActive ? "is-active" : ""
                    }`}
                    ref={heightFractionDropdownRef}
                  >
                    <div
                      className="dropdown-trigger"
                      style={{ overflow: "hidden" }}
                    >
                      <button
                        className={`button is-outlined reset-button is-light ${
                          heightError ? "input-error" : ""
                        }`}
                        id="outside_height_inch_fraction"
                        aria-haspopup="true"
                        aria-controls="init_height-fractions"
                        onClick={() => {
                          setHeightFractionsDropdownActive(
                            !heightFractionsDropdownActive
                          );
                        }}
                      >
                        {dimensionsState.init_height_fraction.numerator ===
                        0 ? (
                          <span>0</span>
                        ) : (
                          <span>
                            {dimensionsState.init_height_fraction.numerator}/
                            {dimensionsState.init_height_fraction.denominator}
                          </span>
                        )}
                        <span className="icon is-small">
                          <FaCaretDown></FaCaretDown>
                        </span>
                      </button>
                    </div>
                    <div
                      className="dropdown-menu"
                      id="init_height-fractions"
                      role="menu"
                    >
                      <div className="dropdown-content has-text-black">
                        {FRACTIONS_V2.map((fraction, index) => (
                          <button
                            className="reset-button dropdown-item"
                            key={"height-fraction-" + index}
                            onClick={() => {
                              dispatch({
                                type: IMPERIAL_ACTIONS.SET_HEIGHT_FRACTION,
                                fraction,
                              });
                              handleChangeHeight("imperial", {
                                fraction,
                                integer: dimensionsState.init_height_integer,
                              });
                              setHeightFractionsDropdownActive(false);
                            }}
                          >
                            {fraction.numerator === 0
                              ? "0"
                              : `${fraction.numerator}/${fraction.denominator}`}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <span className={`unit ${heightError ? "input-error" : ""}`}>
                    in
                  </span>
                </div>
              )}
              <div className={`is-flex ${imperial ? "is-hidden" : ""}`}>
                <input
                  className={`cm-input ${heightError ? "input-error" : ""}`}
                  type="number"
                  step="0.01"
                  min="0"
                  id="init_outside_height"
                  onChange={(e) => handleChangeHeight("metric", e.target.value)}
                />
                <span className={`unit ${heightError ? "input-error" : ""}`}>
                  cm
                </span>
              </div>
            </label>
          </div>
          <div className="dimensionForm--unit">
            <span>Unit</span>
            <label className="unit-switch">
              <input
                type="checkbox"
                onClick={(e) => handleSwitchUnit(e.target.checked)}
                value={imperial}
                defaultChecked={true}
              />
              <span className="imperial">Imperial</span>
              <span className="metric">Metric</span>
            </label>
          </div>
        </div>
        <div>
          {(widthError || depthError || heightError) && (
            <div className="dimensionForm--error-msg">
              {dimensionsErrorMessage}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DimensionForm;
